import { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import {
    resolveEffectiveCorpBudget, scopeOwnDealers, computeCorpQuotaBreakdown, computeNodeQuotaBreakdown,
    extractAllocationFacts, computeDealerRemaining, buildSummaryBadges,
} from '../../../lib/smartDentQuota';
import { computeNodeQuota } from '../../../lib/allocationGraph';
import { buildParentOf } from '../../../lib/allocationContext';
import { formatLicenseDateOnly, isDealerRole } from '../../../lib/licenseHelper';
import { Translate as t } from '../../../config/Translate';
import { SUMMARY_BG, SUMMARY_HIGHLIGHT_BG } from '../../../components/license/summaryStyle';

// 현황 카드 열의 카드 1장(제목 + 수치 + 만료일). 5D·RAYFace 칸(LicenseOverviewTab)도 이 칸을 쓴다.
// onSelect 가 있으면 눌러서 고르는 카드(div role=button — h6/p 를 button 안에 넣지 않으려고), 없으면 현황만 보이는 칸.
export const LicenseStatusCell = ({ name, expireDate, children, selected = false, onSelect }) => {
    const selectable = typeof onSelect === 'function';
    const selectableProps = selectable
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-pressed': selected,
            onClick: onSelect,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            },
        }
        : {};
    return (
        <div
            className={`card rounded px-3 py-2 mb-0 h-100${selected ? ' border-primary' : ''}`}
            style={{
                background: selected ? SUMMARY_HIGHLIGHT_BG : SUMMARY_BG,
                cursor: selectable ? 'pointer' : undefined,
                boxShadow: selected ? '0 0 0 1px var(--bs-primary, #0d6efd)' : undefined,
            }}
            {...selectableProps}
        >
            <h6 className="card-title mb-1">{name}</h6>
            {children}
            {expireDate && (
                <p className="card-text text-muted small mb-0">
                    {t('common.label.expires')}: {formatLicenseDateOnly(expireDate)}
                </p>
            )}
        </div>
    );
};

// 수치 칸 렌더 — 바우처 제품 카드와 flat 제품(5D·RAYFace, LicenseOverviewTab) 공용.
export const LicenseStatusCounts = ({ item }) => {
    if (item.type === 'dealer') {
        // 딜러 자기 뷰(발급현황 Self 탭 AllocationSummaryCard)와 같은 값·라벨. 화면에는 신규·갱신 글자 없이
        // "신규 / 갱신" 순서로 두 수만 적는다(Self 탭 배지·와이어프레임과 같은 표기).
        const rows = [
            ...buildSummaryBadges(item.facts, { includeAllocated: false }).map((b) => ({ key: b.key, labelKey: b.labelKey, qty: b.qty })),
            { key: 'remaining', labelKey: 'license.label.redeemableRemainder', qty: item.remaining },
        ];
        return rows.map((r) => (
            <p key={r.key} className="card-text mb-1" data-testid={`overview-dealer-${r.key}`}>
                {t(r.labelKey)}: <strong>{r.qty?.newQty ?? 0}</strong> / <strong>{r.qty?.renewQty ?? 0}</strong>
            </p>
        ));
    }
    if (item.type === 'split') {
        return (
            <>
                <p className="card-text mb-1" data-testid="overview-initial-label">
                    {t(item.initialLabelKey)}: <strong>{item.initialRemaining}</strong>
                </p>
                <p className="card-text mb-1" data-testid="overview-renewal-label">
                    {t(item.renewalLabelKey)}: <strong>{item.renewalRemaining}</strong>
                </p>
                {/* BUG-191: 바우처 제품은 하위 배분(Σd)만 차감한 "배분 가능 잔여"(t−Σd)라
                    법인탭 Allocation Summary 와 동일한 보조 문구(Total − Σ Allocated)를 표기.
                    flat 제품은 showAllocatableCaption 이 없어 생략. */}
                {item.showAllocatableCaption && (
                    <small className="text-muted d-block mt-1">
                        {t('license.label.allocatableToSubOrgs')} (= {t('common.label.total')} − Σ {t('license.status.allocated')})
                    </small>
                )}
            </>
        );
    }
    const remaining = (isNaN(item.t_package) ? 0 : item.t_package) - (isNaN(item.u_package) ? 0 : item.u_package);
    return (
        <p className="card-text mb-1" data-testid="overview-single-label">
            {t(item.singleLabelKey)}: <strong>{remaining}</strong>
        </p>
    );
};

// 바우처 제품(LICENSE_PRODUCTS) 1장의 라이선스 현황 카드 — 제품마다 같은 규칙으로 계산한다.
const LicenseProductStatusCard = ({ product, user, role, selected, onSelect, licenses, myReceivedAllocation, dealerAllocations, companies, allocationNodes, scopeContext }) => {
    const dispatch = useDispatch();
    const isDealer = isDealerRole(role);

    useEffect(() => {
        if (!user?.companyId) return;
        // 딜러 자기 뷰(LicenseDealerTab 딜러 경로)와 같이 받은 레코드 하나만 조회한다.
        if (isDealer) {
            dispatch(SmartDentAction.GetMyDealerAllocation(user.companyId, { product, receivedSlotOnly: true }));
            return;
        }
        // BUG-177: 중간 법인은 자기 corp 원장이 비고 예산이 상위에게서 받은 레코드에 있음 — 병행 조회.
        // IMP-390: GetMyDealerAllocation 을 병렬 dispatch 하고 그 promise 를 GetDealerAllocations 에 전달.
        // mg-present 경로는 receivedRecord 를 참조 안 하므로 지연 없음(병렬 유지). mg-null 경로만
        // in-flight promise 를 await 해 중복 broad scan(queryDealerAllocationByDealerId) 제거.
        // receivedSlotOnly: 발급현황(Self) 탭의 dealerAllocation 칸을 덮지 않고 받은 예산 칸만 채운다.
        // IMP-386: Overview 도 자기 하위 배분(Σd)을 조회해 자기 여유분(t−u−Σd) 산출.
        // packageId 는 queryDealerAllocations 에서 미사용(_packageId) → null 전달.
        const receivedPromise = dispatch(SmartDentAction.GetMyDealerAllocation(user.companyId, { product, receivedSlotOnly: true }));
        dispatch(SmartDentAction.GetDealerAllocations(null, user.companyId, { receivedRecord: receivedPromise, product }));
        // IMP-420: 노드 모델(N단계) 서브트리 조회 — 표시 소비처 전환 병행 배선.
        // 현황 카드는 selectedPackage 가 없어 기존 null 패턴(GetDealerAllocations 와 동일)을 따른다.
        dispatch(SmartDentAction.GetAllocationSubtree(null, user.companyId, { product }));
    }, [dispatch, user?.companyId, product, isDealer]);

    // IMP-420: LicenseSubsidiaryTab 과 동일 배선 — 노드 모델 우선, 미해석 시 기존 2-tier 폴백.
    // m4: GetAllocationSubtree 가 자기 자신을 루트로 태깅하므로 effectiveOwner ∈ {자기, null} —
    // parentOf 는 pool-participant 해석이 없어 결과 불변(구조 일관 위해 Subsidiary 와 동일 배선 유지).
    const parentOf = useMemo(() => buildParentOf(companies), [companies]);
    const hasAllocationNodes = !!allocationNodes && Object.keys(allocationNodes).length > 0;
    const nodeBreakdown = useMemo(
        () => computeNodeQuotaBreakdown(user?.companyId, { nodesByCompanyId: allocationNodes, parentOf }),
        [user?.companyId, allocationNodes, parentOf],
    );
    const useNodeModel = hasAllocationNodes && !!nodeBreakdown.effectiveOwner;

    // t_initial/t_renewal 구조이면 split 타입, 아니면 single 타입(0 표시)으로 분류.
    const item = useMemo(() => {
        // 딜러: 받은 레코드의 t·u 만 쓴다(t−u, 발급 누적 i 미사용). 레코드가 없으면 0 / 0.
        if (isDealer) {
            return {
                type: 'dealer',
                facts: extractAllocationFacts(myReceivedAllocation ?? {}),
                remaining: computeDealerRemaining(myReceivedAllocation),
            };
        }
        const found = (licenses || []).find((l) => l._id === product.licenseAppId);
        // BUG-177: 중간 법인은 자기 원장(found)이 비어있고 예산이 상위에게서 받은 레코드(myReceivedAllocation)에 있음 — override.
        // effectiveCorp/isSubEntity 모두 필요하므로 1회 해석 후 재사용(중복 호출 방지).
        const budget = resolveEffectiveCorpBudget(null, myReceivedAllocation, user?.companyId, found, scopeContext);
        const src = budget.effectiveCorp;
        // IMP-420: 노드 모델 사용 시 legacy src(found/myReceivedAllocation) 없이 allocationNodes
        // 만으로도 값이 나오므로, useNodeModel 이면 legacy src 부재와 무관하게 split 카드로 판정.
        const isSplitType = useNodeModel || (src && (src.t_initial !== undefined || src.t_renewal !== undefined));
        if (isSplitType) {
            let initialRemaining;
            let renewalRemaining;
            // FR-009: 라벨은 값 계산 경로와 같은 분기에서 정한다(selfUsable / unallocated).
            let initialLabelKey = 'license.label.initialSelfUsable';
            let renewalLabelKey = 'license.label.renewalSelfUsable';
            if (useNodeModel) {
                // IMP-420: 게이트 제거 — 흡수(buildNodesByCompanyId)가 corp 원장(2-seg)의
                // 롤업 u/i 를 버리고 셀프딜링만 흡수하도록 고쳐져 node.u 가 항상 "자기소모"가
                // 된다. selfUsable(t-u-Σ자식.t) 단일 공식이 최상위/중간법인 모두 정합.
                initialRemaining = nodeBreakdown.initial.selfUsable;
                renewalRemaining = nodeBreakdown.renewal.selfUsable;
            } else {
                // 자기 여유분 = t − u − Σ(내가 배분한 dealer.t). resolveEffectiveCorpBudget 의
                // isSubEntity 로 중간 법인 판정 후 scopeOwnDealers 로 자기 배분분만 스코프(SSoT 공유).
                const scoped = scopeOwnDealers(dealerAllocations, user?.companyId, budget.isSubEntity);
                // BUG-191: 최상위 법인은 "Allocatable to sub-orgs = t − Σ(dealer.t)"(배분 가능량)을
                // 표시한다. corp 자기 redeem(u)은 배분이 아니라 소비이므로 이 지표의 차감 대상이
                // 아니다(computeCorpQuotaBreakdown().allocatable, t−Σd).
                // FEAT-050 (나) Increment 2 첫 슬라이스: 중간 법인(isSubEntity)은 상위에서 받은
                // 예산을 자기가 직접 소비(u)할 수도 있으므로 selfUsable(t−u−Σd, computeNodeQuota
                // 제공)을 "진짜 배분 가능 잔여"로 표시. 최상위는 allocatable 유지(무회귀).
                const nodeQuota = computeNodeQuota(src, scoped);
                if (budget.isSubEntity) {
                    initialRemaining = nodeQuota.initial.selfUsable;
                    renewalRemaining = nodeQuota.renewal.selfUsable;
                } else {
                    const bd = computeCorpQuotaBreakdown(src, scoped);
                    initialRemaining = bd.allocatable.newQty;
                    renewalRemaining = bd.allocatable.renewQty;
                    initialLabelKey = 'license.label.initialUnallocated';
                    renewalLabelKey = 'license.label.renewalUnallocated';
                }
            }
            return {
                type: 'split',
                initialRemaining,
                renewalRemaining,
                initialLabelKey,
                renewalLabelKey,
                expireDate: src?.expireDate,
                // 보조 캡션(allocatableToSubOrgs, t−Σd)은 값이 그 식(allocatable)일 때만 붙인다 —
                // 중간 법인·노드 모델은 값이 selfUsable(t−u−Σd)이라 숨긴다.
                showAllocatableCaption: !budget.isSubEntity && !useNodeModel,
            };
        }
        return {
            type: 'single',
            t_package: src?.t_package ?? 0,
            u_package: src?.u_package ?? 0,
            // FR-009: 단일 카드(t_package − u_package)는 Σ하위가 없는 t−u = Self-Usable 로 수렴.
            singleLabelKey: 'license.label.selfUsable',
            expireDate: src?.expireDate,
        };
    }, [isDealer, licenses, product, myReceivedAllocation, dealerAllocations, user?.companyId, useNodeModel, nodeBreakdown, scopeContext]);

    return (
        <LicenseStatusCell name={product?.name} expireDate={item.expireDate} selected={selected} onSelect={onSelect}>
            <LicenseStatusCounts item={item} />
        </LicenseStatusCell>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps.product.key);
    return {
        licenses: state.LicenseReducer.licenses,
        myReceivedAllocation: licenseState.myReceivedAllocation,
        dealerAllocations: licenseState.dealerAllocations,
        companies: state.CompanyReducer?.companies,
        allocationNodes: licenseState.allocationNodes,
        scopeContext: licenseState.scopeContext,
    };
};

export default connect(mapState)(LicenseProductStatusCard);
